import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { formatDateDisplay, formatPlatform } from "../lib/utils";
import { Clock, CheckCircle2, Tag } from "lucide-react";
import Loading from "../components/Loading";

export default function PreOrderSuccessPage() {
  const { id } = useParams();
  const [po, setPo] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/preorder/${id}`);
        setPo(r.data);
      } catch {
        setPo(false);
      }
    })();
  }, [id]);

  if (po === false) return <div className="p-12 text-center text-slate-400">Pre-order not found.</div>;
  if (!po) return <Loading variant="page" label="Loading pre-order" />;

  return (
    <div className="max-w-3xl mx-auto px-6 py-16">
      <Helmet><meta name="robots" content="noindex, nofollow" /></Helmet>
      <div className="text-center mb-10">
        <CheckCircle2 className="w-16 h-16 text-gold mx-auto mb-4" />
        <h1 className="text-4xl font-black uppercase tracking-tighter">Pre-Order <span className="text-gold">Confirmed!</span></h1>
        <p className="text-slate-300 mt-2 text-lg">Thanks for shopping with us! 🎮</p>
        <p className="text-slate-400 mt-1 text-sm">Your pre-order <span className="text-gold font-bold">#{po.order_number}</span> has been confirmed.</p>
        <p className="text-slate-400 mt-1 text-sm">We'll notify you at <span className="text-gold font-semibold">{po.customer_email}</span> when it's ready.</p>
      </div>

      <div className="bg-navy-700 rounded-xl p-6 border border-gold/40 space-y-5">
        {/* PRE-ORDER tag */}
        <div className="flex items-center gap-2">
          <span className="bg-gold text-navy-900 text-[10px] font-black uppercase tracking-[0.15em] px-3 py-1 rounded">PRE-ORDER</span>
          {po.release_date && (
            <span className="text-[11px] text-slate-400 flex items-center gap-1">
              <Clock className="w-3 h-3" />
              Releases {formatDateDisplay(po.release_date)}
            </span>
          )}
        </div>

        <div className="border-t border-gold/10 pt-4 space-y-3 text-sm">
          {(po.items || []).map((it, i) => (
            <div key={i} className="flex gap-3 items-center">
              {it.image && <img src={it.image} alt="" className="w-12 h-12 object-cover rounded-lg shrink-0" />}
              <div className="flex-1 min-w-0">
                <div className="font-bold">{it.name}</div>
                <div className="text-xs text-slate-400 mt-0.5">
                  Qty {it.quantity}
                  {it.platform && <span className="ml-1">· {formatPlatform(it.platform)}</span>}
                  {it.condition && <span className="ml-1">· {it.condition.charAt(0).toUpperCase() + it.condition.slice(1)}</span>}
                </div>
              </div>
              <div className="font-black text-gold">₹{(it.price * it.quantity).toLocaleString()}</div>
            </div>
          ))}
          <div className="border-t border-gold/20 pt-4 space-y-1 text-sm">
            <div className="flex justify-between"><span className="text-slate-400">Subtotal</span><span>₹{po.subtotal.toLocaleString()}</span></div>
            <div className="flex justify-between"><span className="text-slate-400">Shipping</span><span>{po.shipping === 0 ? "FREE" : `₹${po.shipping}`}</span></div>
            {po.coupon_code && (
              <div className="flex justify-between text-emerald-400">
                <span className="flex items-center gap-1.5"><Tag className="w-3.5 h-3.5" />{po.coupon_code}</span>
                <span>−₹{(po.coupon_discount || 0).toLocaleString()}</span>
              </div>
            )}
            <div className="flex justify-between text-lg font-black mt-2"><span>Total</span><span className="text-gold">₹{po.total.toLocaleString()}</span></div>
          </div>
        </div>

        <div className="text-xs text-slate-400">Shipping to: {po.shipping_address}, {po.city}, {po.state} - {po.pincode}</div>

        <div className="bg-navy-800 rounded-lg px-4 py-3 text-[11px] text-slate-400 leading-relaxed">
          We'll reach out once the product is available and ready to ship.
        </div>
      </div>

      <div className="text-center mt-10 flex flex-col sm:flex-row gap-3 justify-center">
        <Link to={`/track?q=${po.order_number}`} className="inline-block bg-navy-700 border border-gold/30 text-gold font-extrabold uppercase tracking-wider text-sm px-7 py-3 rounded-full hover:border-gold transition-all">Track Order</Link>
        <Link to="/shop" className="inline-block bg-gold text-navy-900 font-extrabold uppercase tracking-wider text-sm px-7 py-3 rounded-full hover:bg-gold-light btn-glow transition-all">Continue Shopping</Link>
      </div>
    </div>
  );
}
